import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import { TextField, Button, ButtonGroup } from '@mui/material';
import {
  useAsignacionManual,
  useAsignacionAuto,
  usePlanificadorCentro,
} from '../../../../api/aulas';
import { useCentros } from '../../../../hooks/useCentros';
import { useClientes } from '../../../../hooks/useClientes';
import { useUsuarios } from '../../../../hooks/useUsuarios';

// ⬇️ Vista “Ocupación (día)”
import { AulaCalendarDay } from '../../../../components/aulas/AulaCalendarDay';

function join(date: string, t: string) {
  return `${date}T${t}`;
}
function overlaps(h1: string, h2: string, H1: string, H2: string) {
  return !(h2 <= H1 || h1 >= H2);
}

export default function PlanificadorCentroPage() {
  const params = useParams<{ id: string }>();
  const centroId = useMemo(() => Number(params.id), [params.id]);

  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const { data, isLoading, error } = usePlanificadorCentro(centroId, fecha);

  const assignManual = useAsignacionManual();
  const autoAssign = useAsignacionAuto();
  const pendingRef = useRef<HTMLDivElement>(null);

  // Conmutador de vista: 'calendar' o 'bars'
  const [viewMode, setViewMode] = useState<'calendar' | 'bars'>('calendar');

  // Nombres de centro, alumnado y profesorado para sustituir IDs en la UI
  const { rows: centros } = useCentros();
  const centroNombre = useMemo(
    () => centros.find((c) => c.id === centroId)?.nombre ?? '',
    [centros, centroId],
  );

  const { rows: clientes } = useClientes();
  const clientesById = useMemo(
    () => new Map<number, string>(clientes.map((c) => [c.id, c.nombreCompleto])),
    [clientes],
  );

  const { rows: profesores } = useUsuarios({ rol: 'profesor' });
  const profesoresById = useMemo(
    () => new Map<number, string>(profesores.map((p) => [p.id, p.nombreCompleto || p.email])),
    [profesores],
  );

  // Helper: intenta extraer nombre(s) de alumno de múltiples formas posibles
  const extractAlumnoNames = (r: any): string[] => {
    const names: string[] = [];

    // Campos directos de nombre
    const nameFields = [
      'alumnoNombre',
      'alumno_nombre',
      'cliente_nombre',
      'clienteNombre',
      'nombre_alumno',
      'nombreAlumno',
      'alumno',
    ];
    for (const f of nameFields) {
      const v = r?.[f];
      if (typeof v === 'string' && v.trim()) names.push(v.trim());
    }

    // Arrays de nombres
    const nameArrays = ['alumnos_nombres', 'alumnosNombres', 'alumnoNombres', 'nombres_alumnos'];
    for (const f of nameArrays) {
      const arr = r?.[f];
      if (Array.isArray(arr)) names.push(...arr.filter(Boolean));
    }

    // IDs simples
    const idFields = ['id_cliente', 'cliente_id', 'idAlumno', 'alumno_id'];
    for (const f of idFields) {
      const id = Number(r?.[f]);
      if (id) {
        const n = clientesById.get(id);
        if (n) names.push(n);
      }
    }

    // Arrays de IDs o mixed
    const idArrays = ['alumno_ids', 'alumnos', 'alumnos_ids'];
    for (const f of idArrays) {
      const arr = r?.[f];
      if (Array.isArray(arr)) {
        for (const it of arr) {
          const id = typeof it === 'number' ? it : it?.id ? Number(it.id) : NaN;
          if (!Number.isNaN(id)) {
            const n = clientesById.get(id);
            if (n) names.push(n);
          } else if (typeof it === 'string') {
            names.push(it);
          }
        }
      }
    }

    // Evitar duplicados y limitar para no alargar excesivamente
    return Array.from(new Set(names)).slice(0, 3);
  };

  // Helper: nombre del profesor
  const extractProfesorName = (r: any): string | null => {
    const direct =
      r?.profesorNombre || r?.profesor_nombre || r?.profesor?.nombreCompleto || r?.profesor?.nombre;
    if (typeof direct === 'string' && direct.trim()) return direct.trim();

    const pid = Number(r?.id_profesor ?? r?.profesor_id);
    if (pid) {
      const n = profesoresById.get(pid);
      if (n) return n;
    }
    return null;
  };

  // Título legible de la reserva (sin usar #id)
  const buildReservaTitle = (r: any): string => {
    const alumnos = extractAlumnoNames(r);
    const prof = extractProfesorName(r);
    const parts: string[] = [];

    if (alumnos.length) parts.push(alumnos.join(', '));
    if (prof) parts.push(prof);

    const grupo = r?.grupoNombre || r?.grupo?.nombre || r?.asignatura || r?.curso || r?.titulo;
    if (!alumnos.length && grupo) parts.push(grupo);

    const hIni = (r?.hora_inicio ?? '').slice(0, 5);
    const hFin = (r?.hora_fin ?? '').slice(0, 5);
    if (hIni && hFin) parts.push(`${hIni}–${hFin}`);

    if (typeof r?.num_alumnos === 'number') parts.push(`${r.num_alumnos} alumnos`);

    return parts.filter(Boolean).join(' · ') || 'Reserva';
  };

  useEffect(() => {
    if (!pendingRef.current) return;
    const draggable = new Draggable(pendingRef.current, {
      itemSelector: '.pendiente-item',
      eventData: (el) => ({
        title: el.getAttribute('data-title') ?? 'Reserva',
      }),
    });
    return () => draggable.destroy();
  }, [data?.reservas]);

  if (!centroId) return <div>Centro no válido</div>;
  if (isLoading) return <div>Cargando planificador…</div>;
  if (error || !data) return <div>Error cargando datos</div>;

  const resources = (data.aulas ?? []).map((a) => ({
    id: String(a.id),
    title: `${a.nombre} (${a.capacidad}) · ${a.uso_aula ?? 'MIXTA'} · ${a.internet_perfil ?? 'BASICO'}`,
  }));

  // Solo mostramos/arrastramos reservas ASIGNADAS como eventos del calendario
  const events = (data.reservas ?? [])
    .filter((r) => r.id_aula != null && r.aula_estado === 'ASIGNADA')
    .map((r) => ({
      id: String(r.id),
      title: buildReservaTitle(r),
      start: join(r.fecha, r.hora_inicio),
      end: join(r.fecha, r.hora_fin),
      resourceId: String(r.id_aula),
    }));

  // ⬇️ Pendientes: blindamos por centro si el dato viene; si no viene, dejamos pasar
  const pendientes = (data.reservas ?? []).filter((r) =>
    (r.aula_estado === 'PENDIENTE' || (!r.id_aula && r.aula_estado !== 'NO_REQUIERE')) &&
    (r as any).id_centro != null
      ? Number((r as any).id_centro) === centroId
      : true,
  );

  // Helpers para bloqueos
  const overlapsFn = overlaps;
  const eventAllowFn = (dropInfo: any, dragged: any) => {
    const resourceId =
      (dropInfo as any).resource?.id ?? (dragged as any)?.getResources?.()?.[0]?.id;
    const aulaId = Number(resourceId);
    if (!aulaId) return false;

    const fechaDrop = dropInfo.startStr.slice(0, 10);
    const hIni = dropInfo.startStr.slice(11, 19);
    const hFin = dropInfo.endStr.slice(11, 19);
    const dow = new Date(`${fechaDrop}T00:00:00`).getDay(); // 0=Dom

    const bloqueos = (data.bloqueos ?? []).filter(
      (b) =>
        b.id_aula === aulaId &&
        b.activo &&
        b.dow === dow &&
        fechaDrop >= b.fecha_inicio &&
        fechaDrop <= b.fecha_fin &&
        overlapsFn(hIni, hFin, b.hora_inicio, b.hora_fin),
    );
    return bloqueos.length === 0;
  };

  const resourceHeader = centroNombre ? `Aulas — ${centroNombre}` : 'Aulas';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16 }}>
      <div>
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <TextField
            type="date"
            label="Fecha"
            size="small"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          {/* ⬇️ Conmutador de vista */}
          <ButtonGroup size="small" aria-label="Modo de vista">
            <Button
              variant={viewMode === 'calendar' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('calendar')}
            >
              Calendario
            </Button>
            <Button
              variant={viewMode === 'bars' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('bars')}
            >
              Ocupación (día)
            </Button>
          </ButtonGroup>
        </div>

        {viewMode === 'calendar' ? (
          <FullCalendar
            plugins={[resourceTimelinePlugin, interactionPlugin]}
            initialView="resourceTimelineDay"
            slotMinTime="08:00:00"
            slotMaxTime="22:00:00"
            resourceAreaHeaderContent={resourceHeader}
            resources={resources}
            events={events}
            editable
            droppable
            eventAllow={eventAllowFn}
            // Arrastre desde "Pendientes"
            eventReceive={async (info) => {
              const aulaId = Number(
                (info as any).resource?.id ?? (info.event as any).getResources?.()[0]?.id,
              );
              const reservaId = Number(info.draggedEl?.getAttribute('data-reserva-id'));

              // Blindaje extra: si la reserva trae id_centro y no coincide, revertimos
              const resv = (data.reservas ?? []).find((r) => Number(r.id) === reservaId) as
                | any
                | undefined;
              if (resv && resv.id_centro != null && Number(resv.id_centro) !== centroId) {
                info.revert();
                return;
              }

              if (!aulaId || !reservaId) {
                info.revert();
                return;
              }
              try {
                await assignManual.mutateAsync({ reservaId, aulaId });
              } catch {
                info.revert();
              }
            }}
            // Mover una reserva ya asignada a otra aula
            eventDrop={async (info) => {
              const aulaId = Number(
                (info as any).newResource?.id ?? (info.event as any).getResources?.()[0]?.id,
              );
              const reservaId = Number(info.event.id);
              if (!aulaId || !reservaId) {
                info.revert();
                return;
              }
              try {
                await assignManual.mutateAsync({ reservaId, aulaId });
              } catch {
                info.revert();
              }
            }}
          />
        ) : (
          // ⬇️ Vista simple de ocupación por día (barras)
          <AulaCalendarDay centroId={centroId} fecha={fecha} />
        )}
      </div>

      {/* Bandeja de PENDIENTES con drag & drop */}
      <aside>
        <h3>Pendientes ({pendientes.length})</h3>
        <div id="pendientes" ref={pendingRef}>
          {pendientes.map((p) => {
            const titulo = buildReservaTitle(p);
            return (
              <div
                key={p.id}
                className="pendiente-item"
                draggable
                data-reserva-id={p.id}
                data-title={titulo}
                style={{
                  border: '1px dashed #bbb',
                  padding: 8,
                  marginBottom: 8,
                  borderRadius: 6,
                  cursor: 'grab',
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                title={titulo}
              >
                <span>{titulo}</span>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => autoAssign.mutate(p.id)}
                  disabled={autoAssign.isPending}
                >
                  Auto
                </Button>
              </div>
            );
          })}
          {!pendientes.length && <p>Sin pendientes para {fecha}</p>}
        </div>
      </aside>
    </div>
  );
}
